import {
    Alert20Regular,
    ArrowHookUpLeft20Regular,
    Emoji20Regular,
    FluentIcon,
    LauncherSettings20Regular,
    Question20Filled,
    ShieldError20Regular
} from "@fluentui/react-icons";
import React, { useCallback, useMemo } from "react";
import { useAppSettings, useLogging } from "Shared/Hooks";

import { AppMenuFlyoutBlankContent } from "./Flyouts/AppMenuFlyoutBlankContent";
import { AppMenuHelp } from "./Flyouts/AppMenuHelp";
import { AppMenuSetting } from "./Flyouts/AppMenuSetting";
import { AppRegistry } from "Apps/AppRegistry";
import { IAppMenuFlyoutProps } from "./Flyouts/AppMenuFlyout.types";
import { LogCategory } from "Enumerations";
import { OpsGenieIcon } from "Apps/OpsGenie/Components/OpsGenieIcon/OpsGenieIcon";
import { Paths } from "Shared/Utils";
import { TeamAppEnvs } from "Apps/AppRegistryEnvs";
import { UserFeedback } from "../UserFeedback";
import { useGlobalSetting } from "Store/GlobalSettingStore";
import { useRootStore } from "Store/RootStore";

export enum HeaderIconItemType {
    Feedback,
    CreateIncident,
    Setting,
    Help,
    Notification,
    Back,
    OpsGenie
}

export interface HeaderIconItem {
    key: HeaderIconItemType;
    displayName: string;
    icon: FluentIcon;
    flyoutContent?: (props: IAppMenuFlyoutProps) => React.ReactElement;
    onClick?: (e?: React.MouseEvent | React.KeyboardEvent, targetElement?: HTMLElement | null) => void;
    isHidden?: boolean;
    isHiddenOnLargeScreen?: boolean;
    isHiddenOnSmallScreen?: boolean;
}

export const useHeaderIconItems = (): HeaderIconItem[] => {
    const { icmUrl, NODE_ENV } = useAppSettings();
    const currentAppTitle = useRootStore("AppSettingStore").currentApp.title;
    const { trackEvent } = useLogging(LogCategory.Core);
    const onRedirectToV1 = useCallback(() => {
        trackEvent("e6778c08-0b21-42aa-b696-42a08d7f8b4f", "Switch_Back_To_V1_From_V2_Small_Screen_App_Header");
        const currentState: unknown = window.history.state;
        window.history.pushState(currentState, "", Paths.getFullPath("/apphub/legacy"));
        window.dispatchEvent(new PopStateEvent("popstate", { state: currentState }));
    }, [trackEvent]);
    const opsGenieStore = useGlobalSetting("opsGenieApp");

    const opsGenieEnvs = TeamAppEnvs.get("OpsGenie") ?? [];
    const isOpsGenieAvailable = currentAppTitle !== AppRegistry.OpsGenie.title && opsGenieEnvs.some((env) => env.toLowerCase() === NODE_ENV?.toLowerCase());

    return useMemo(
        () => [
            {
                key: HeaderIconItemType.OpsGenie,
                displayName: "OpsGenie Copilot",
                icon: OpsGenieIcon,
                onClick: () => {
                    opsGenieStore.setIsRightPanelOpen(true);
                },
                isHidden: !isOpsGenieAvailable,
                isHiddenOnSmallScreen: true
            },
            {
                key: HeaderIconItemType.Notification,
                displayName: "Notification",
                icon: Alert20Regular,
                flyoutContent: AppMenuFlyoutBlankContent,
                isHidden: true
            },
            {
                key: HeaderIconItemType.Feedback,
                displayName: "Feedback",
                icon: Emoji20Regular,
                flyoutContent: UserFeedback
            },
            {
                key: HeaderIconItemType.CreateIncident,
                displayName: "Create an Incident",
                icon: ShieldError20Regular,
                onClick: () => {
                    window.open(`${icmUrl}/imp/v3/incidents/create`);
                }
            },
            {
                key: HeaderIconItemType.Setting,
                displayName: "Setting",
                icon: LauncherSettings20Regular,
                flyoutContent: AppMenuSetting
            },
            {
                key: HeaderIconItemType.Help,
                displayName: "Help",
                icon: Question20Filled,
                flyoutContent: AppMenuHelp
            },
            {
                key: HeaderIconItemType.Back,
                displayName: "Classic Version",
                icon: ArrowHookUpLeft20Regular,
                isHiddenOnLargeScreen: true,
                onClick: onRedirectToV1
            }
        ],
        [icmUrl, isOpsGenieAvailable, onRedirectToV1, opsGenieStore]
    );
};
